import type { RtpCapabilities, RtpCodecCapability } from "mediasoup-client/types";

// The caller sends H264 (preferred) or VP8 — both hardware-encodable on phones and decodable
// natively by every dispatcher browser, so the SFU only ever forwards the stream.
const VIDEO_PREFERENCE = ["video/h264", "video/vp8"];

/**
 * Picks the best codec the caller should SEND (H264 preferred, VP8 fallback).
 *
 * device.rtpCapabilities are RECEIVE (router) capabilities, so we intersect them with the browser's
 * SEND capabilities (RTCRtpSender.getCapabilities) to only pick a codec the browser can actually
 * ENCODE. For H264 we match on PROFILE (profile_idc + constraint flags = first 4 hex of
 * profile-level-id), NOT the level: an iOS/Safari caller that only encodes Constrained Baseline
 * still selects the router's higher-level (L5.0) CB codec under level-asymmetry-allowed — that is
 * what lets the phone encode crisp 1080p instead of self-throttling at a 720p-class level.
 *
 * Producing with a codec/profile the browser can't encode makes produce() fail, hence the
 * capability intersection. See Docs/External/WebRTC/ng-sos-video-parameters.md §2.
 */
export function selectSendVideoCodec(receiveCapabilities: RtpCapabilities): RtpCodecCapability {
  const receiveCodecs = receiveCapabilities.codecs ?? [];
  const encodableH264Profiles = h264ProfilesTheBrowserCanEncode();

  for (const mimeType of VIDEO_PREFERENCE) {
    const matches = receiveCodecs.filter(c => c.mimeType.toLowerCase() === mimeType);
    if (matches.length === 0) {
      continue;
    }
    if (mimeType !== "video/h264") {
      return matches[0]; // VP8: mimeType is enough
    }
    // H264: prefer a profile the browser can encode. If send caps were unreadable, trust the
    // router's first H264 (its profiles are all phone-encodable).
    if (encodableH264Profiles.size === 0) {
      return matches[0];
    }
    const encodable = matches.find(c => {
      const idc = h264ProfileIdc(c.parameters?.["profile-level-id"]);
      return idc !== undefined && encodableH264Profiles.has(idc);
    });
    if (encodable) {
      return encodable;
    }
    // No H264 profile is encodable by this browser — fall through to VP8.
  }

  const fallback =
    receiveCodecs.find(c => c.mimeType.toLowerCase() === "video/h264") ??
    receiveCodecs.find(c => c.mimeType.toLowerCase() === "video/vp8");
  if (!fallback) {
    throw new Error("No supported video codec to send (H.264 or VP8)");
  }
  return fallback;
}

// The profile part of an H264 profile-level-id: profile_idc + constraint flags (first 4 hex),
// without the trailing 2-hex level. Same-profile-different-level codecs share this value (the level
// is negotiated separately under level-asymmetry-allowed).
function h264ProfileIdc(profileLevelId: unknown): string | undefined {
  return typeof profileLevelId === "string" && profileLevelId.length >= 4
    ? profileLevelId.slice(0, 4).toLowerCase()
    : undefined;
}

/** Set of H264 profile ids (first 4 hex) the browser can ENCODE; empty if capabilities are unreadable. */
function h264ProfilesTheBrowserCanEncode(): Set<string> {
  const profiles = new Set<string>();
  try {
    const caps =
      typeof RTCRtpSender !== "undefined" && RTCRtpSender.getCapabilities
        ? RTCRtpSender.getCapabilities("video")
        : null;
    for (const codec of caps?.codecs ?? []) {
      if (codec.mimeType.toLowerCase() !== "video/h264") {
        continue;
      }
      const idc = h264ProfileIdc(codec.sdpFmtpLine?.match(/profile-level-id=([0-9a-fA-F]{6})/)?.[1]);
      if (idc) {
        profiles.add(idc);
      }
    }
  } catch {
    /* leave empty: caller falls back to the router's first H264 */
  }
  return profiles;
}
